import { useMemo, useEffect } from 'react';
import { isEmpty, isNil } from 'lodash';
// Import Types
import type { ConfigurationColumnData } from 'types';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import { clearReportColumns, selectReportColumns, setReportColumns } from 'store/slices/reportColumns';
// Import Hooks
import useRequest from './UseRequest';
// Import Services
import { UtilsHttpService } from 'services/http';
import { subscribe } from 'services/event';
// Import Constants
import { GlobalEvents, SubscriberSources } from 'constants/common';

let filteredColumns = new WeakMap();
let isRequested = false;

const getFilteredColumns = (
    predicate: Nilable<(columns: ConfigurationColumnData[]) => ConfigurationColumnData[]> = undefined,
    reportColumns: ConfigurationColumnData[],
): ConfigurationColumnData[] => {
    if (isNil(predicate) || isEmpty(reportColumns)) {
        return reportColumns;
    }

    if (!filteredColumns.has(predicate)) {
        filteredColumns.set(predicate, predicate([...reportColumns]));
    }

    return filteredColumns.get(predicate);
};

/**
 * @typedef {Object} ReportColumn
 */

/**
 * A custom React Hook that provides access to report columns and allows filtering based on a predicate logic.
 * @param {(columns: ConfigurationColumnData[]) => ConfigurationColumnData[]} [predicate] An optional predicate function used to filter the report columns. It should accept a column as a parameter and return a boolean.
 * If provided, only columns satisfying the predicate will be included in the result.
 * @returns {[ReportColumn[], boolean]}
 */
const useReportColumns = (predicate?: (columns: ConfigurationColumnData[]) => ConfigurationColumnData[]) => {
    const { doGetRequest } = useRequest();
    const dispatch = useAppDispatch();

    const reportColumns = useAppSelector(selectReportColumns);

    const { getAllKPIs } = useMemo(
        () => ({
            // eslint-disable-next-line no-restricted-syntax -- an abort would leave the module-level isRequested flag stuck
            getAllKPIs: UtilsHttpService.getAllKPIs(),
        }),
        [],
    );

    useEffect(() => {
        if (isEmpty(reportColumns) && !isRequested) {
            isRequested = true;

            const resetReportColumns = () => {
                dispatch(clearReportColumns());
                filteredColumns = new WeakMap();
                isRequested = false;
            };

            subscribe(GlobalEvents.PartnerChanged, SubscriberSources.ReportColumn, resetReportColumns);
            subscribe(GlobalEvents.LanguageChanged, SubscriberSources.ReportColumn, resetReportColumns);

            doGetRequest(getAllKPIs.request, {
                successCallback: (columns) => {
                    dispatch(setReportColumns(columns));
                },
            });
        }
    }, [dispatch, doGetRequest, getAllKPIs.request, reportColumns]);

    return [getFilteredColumns(predicate, reportColumns), isEmpty(reportColumns)] as const;
};

export default useReportColumns;
